import { validatedDisplayView } from "../../i18n/display-view.js";

/**
 * Area `survey` of the console gateway — plan-002 unit **RB-26** (design §15.6.6), AMENDED 2026-10-03
 * (design §16.7.2 AS BUILT 2026-10-03).
 *
 * WHAT CHANGED ON 2026-10-03. This slice used to read survey ROUNDS (`listSurveyRounds`,
 * `getSurveyResult(roundId)`, …) over `campaign_response`. Nothing ever wrote a round or a campaign
 * response: the survey interviewer files one `survey_answer` row per question, and sets the
 * conversation's outcome. So the console now reads the four operations that serve those answers:
 *
 *   getSurveyResult(voiceAgentId, filter)            getVoiceAgentSurveyResult
 *   listSurveyRespondents(voiceAgentId, filter, …)   listVoiceAgentSurveyRespondents
 *   requestSurveyDownload(request)                   createVoiceAgentSurveyExport
 *   getConversationSurveyAnswer(conversationId)      getConversationSurveyAnswer
 *
 * The round operations stay served by the API (an API key may still call them); the console no longer
 * does, and `listSurveyRounds` is gone from the gateway. The register is EMPTY: every method is served.
 *
 * ── THE FILTER ──────────────────────────────────────────────────────────────────────────────────
 * `range` is the window the page offers ("Last 7 days" …), turned into `started_from` from the injected
 * clock (`context.now`), never the machine's; `all` sends no bound. `include_is_test` is sent as written,
 * so "real conversations only" is the console's explicit choice, not an absent parameter's meaning.
 *
 * ── FACTS IN, WORDS OUT ─────────────────────────────────────────────────────────────────────────
 * Every count and share arrives from the API and is carried as it is; the only text this slice words is
 * a respondent's `startedText` (with `formatWhen`) and the download's expiry. `completion_basis_point`
 * stays `null` when there are no conversations — never 0%.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type {
  ConsoleGateway,
  ConversationSurveyAnswerView,
  SurveyAnswerOptionView,
  SurveyDownloadRequestView,
  SurveyDownloadView,
  SurveyGivenAnswerView,
  SurveyNumberRangeView,
  SurveyNumberSummaryView,
  SurveyQuestionKindView,
  SurveyQuestionTypeView,
  SurveyQuestionResultView,
  SurveyHangUpKindView,
  SurveyRespondentOutcomeView,
  SurveyRespondentPageView,
  SurveyRespondentRowView,
  SurveyResultFilterView,
  SurveyResultRangeView,
  SurveyResultView,
  SurveyVariantView,
} from "../gateway.js";
import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readTextArray,
  readWholeNumber,
} from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    getSurveyResult(voiceAgentId: string, filter: SurveyResultFilterView): Promise<SurveyResultView>;
    listSurveyRespondents(
      voiceAgentId: string,
      filter: SurveyResultFilterView,
      outcome: SurveyRespondentOutcomeView | "all",
      cursor: string | null,
    ): Promise<SurveyRespondentPageView>;
    requestSurveyDownload(request: SurveyDownloadRequestView): Promise<SurveyDownloadView>;
    getConversationSurveyAnswer(conversationId: string): Promise<ConversationSurveyAnswerView>;
  }
}

export const surveyGap: readonly GatewayGap[] = [];

/** The API's maximum page. */
const PAGE_LIMIT = 100;

const HOUR_MS = 3_600_000;

/** How far back each window reaches. `all` reaches back to the first conversation. */
export const SURVEY_RANGE_MS: Readonly<Record<SurveyResultRangeView, number | null>> = {
  "24h": 24 * HOUR_MS,
  "7d": 7 * 24 * HOUR_MS,
  "14d": 14 * 24 * HOUR_MS,
  "30d": 30 * 24 * HOUR_MS,
  "90d": 90 * 24 * HOUR_MS,
  all: null,
};

const OUTCOME: readonly SurveyRespondentOutcomeView[] = ["completed", "partial", "declined", "no_answer", "no_outcome"];
const KIND: readonly SurveyQuestionKindView[] = ["statement", "emotion", "source", "choice", "open", "number"];
const QUESTION_TYPE: readonly SurveyQuestionTypeView[] = ["scale", "choice", "open", "number"];

function readQuestionType(value: unknown, path: string): SurveyQuestionTypeView | null {
  if (value === null) return null;
  const raw = readText(value, path);
  const found = QUESTION_TYPE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a survey question type", raw);
  return found;
}

function readBound(value: unknown, path: string): number | null {
  if (value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value)) throw new MalformedResponseError(path, "a number or null", String(value));
  return value;
}

function numberRangeOf(value: unknown, path: string): SurveyNumberRangeView {
  const one = readRecord(value, path);
  return {
    min: readBound(one["min"], `${path}.min`),
    max: readBound(one["max"], `${path}.max`),
    integer: readBoolean(one["integer"], `${path}.integer`),
    unit: labelsOf(one["unit"], `${path}.unit`, "language_code", "text"),
  };
}

function numberSummaryOf(value: unknown, path: string): SurveyNumberSummaryView {
  const one = readRecord(value, path);
  return {
    count: readWholeNumber(one["count"], `${path}.count`),
    average: readText(one["average"], `${path}.average`),
    minimum: readText(one["minimum"], `${path}.minimum`),
    maximum: readText(one["maximum"], `${path}.maximum`),
  };
}

/** `Today 14:02` reads as `today 14:02` mid-sentence; a date stays as it is. */
function midSentence(whenText: string): string {
  return whenText.replace(/^(Today|Yesterday)/, (word) => word.toLowerCase());
}

function readNullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function readNullableBoolean(value: unknown, path: string): boolean | null {
  return value === null ? null : readBoolean(value, path);
}

function readNullableNumber(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

function readOutcome(value: unknown, path: string): SurveyRespondentOutcomeView {
  const raw = readText(value, path);
  const found = OUTCOME.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a survey outcome", raw);
  return found;
}

function readKind(value: unknown, path: string): SurveyQuestionKindView {
  const raw = readText(value, path);
  const found = KIND.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a survey question kind", raw);
  return found;
}

function readVariant(value: unknown, path: string): SurveyVariantView | null {
  if (value === null) return null;
  const raw = readText(value, path);
  if (raw === "A" || raw === "B") return raw;
  throw new MalformedResponseError(path, "A, B or null", raw);
}

/** The query every results read sends: the filter, exactly. */
export function surveyFilterQueryOf(
  filter: SurveyResultFilterView,
  now: Date,
): {
  include_is_test: string;
  started_from?: string;
  campaign_id?: string;
  language_code?: string;
  variant?: string;
} {
  const reach = SURVEY_RANGE_MS[filter.range];
  return {
    include_is_test: filter.includeIsTest ? "true" : "false",
    ...(reach === null ? {} : { started_from: new Date(now.getTime() - reach).toISOString() }),
    ...(filter.campaignId === null ? {} : { campaign_id: filter.campaignId }),
    ...(filter.languageCode === null ? {} : { language_code: filter.languageCode }),
    ...(filter.variant === null ? {} : { variant: filter.variant }),
  };
}

function labelsOf(value: unknown, path: string, key: string, label: string): Record<string, string> {
  return Object.fromEntries(readArray(value, path).map((entry, index) => {
    const at = `${path}[${String(index)}]`;
    const one = readRecord(entry, at);
    return [readText(one[key], `${at}.${key}`), readText(one[label], `${at}.${label}`)];
  }));
}

function optionOf(value: unknown, path: string): SurveyAnswerOptionView {
  const one = readRecord(value, path);
  return {
    answer: readText(one["answer"], `${path}.answer`),
    count: readWholeNumber(one["count"], `${path}.count`),
    shareBasisPoint: readWholeNumber(one["share_basis_point"], `${path}.share_basis_point`),
    confidentCount: readWholeNumber(one["confident_count"], `${path}.confident_count`),
    unsureCount: readWholeNumber(one["unsure_count"], `${path}.unsure_count`),
    mentionCount: readWholeNumber(one["mention_count"], `${path}.mention_count`),
    ...(one["label"] === undefined ? {} : { label: labelsOf(one["label"], `${path}.label`, "language_code", "text") }),
  };
}

function questionOf(value: unknown, path: string): SurveyQuestionResultView {
  const one = readRecord(value, path);
  const text: Record<string, string> = {};
  readArray(one["text"], `${path}.text`).forEach((entry, index) => {
    const at = `${path}.text[${index}]`;
    const record = readRecord(entry, at);
    text[readText(record["language_code"], `${at}.language_code`)] = readText(record["text"], `${at}.text`);
  });
  return {
    questionCode: readText(one["question_code"], `${path}.question_code`),
    instrumentCode: readText(one["instrument_code"], `${path}.instrument_code`),
    questionKind: readKind(one["question_kind"], `${path}.question_kind`),
    variant: readVariant(one["variant"], `${path}.variant`),
    ordinal: readWholeNumber(one["ordinal"], `${path}.ordinal`),
    text,
    answeredCount: readWholeNumber(one["answered_count"], `${path}.answered_count`),
    ...(one["selection_max"] === undefined ? {} : { selectionMax: readWholeNumber(one["selection_max"], `${path}.selection_max`) }),
    ...(one["question_type"] === undefined ? {} : { questionType: readQuestionType(one["question_type"], `${path}.question_type`) }),
    ...(one["number_range"] === undefined ? {} : { numberRange: numberRangeOf(one["number_range"], `${path}.number_range`) }),
    ...(one["number"] === undefined ? {} : { number: numberSummaryOf(one["number"], `${path}.number`) }),
    option: readArray(one["option"], `${path}.option`).map((each, index) => optionOf(each, `${path}.option[${index}]`)),
  };
}

function givenAnswerOf(value: unknown, path: string): SurveyGivenAnswerView {
  const one = readRecord(value, path);
  return {
    questionCode: readText(one["question_code"], `${path}.question_code`),
    questionKind: readKind(one["question_kind"], `${path}.question_kind`),
    answer: readText(one["answer"], `${path}.answer`),
    mentioned: readTextArray(one["mentioned"], `${path}.mentioned`),
    ...(one["option_label"] === undefined ? {} : { optionLabel: labelsOf(one["option_label"], `${path}.option_label`, "answer", "label") }),
    ...(one["open_text"] === undefined ? {} : { openText: readText(one["open_text"], `${path}.open_text`) }),
    confident: readNullableBoolean(one["confident"], `${path}.confident`),
    languageCode: readText(one["language_code"], `${path}.language_code`),
  };
}

/**
 * plan-033 WAPP-HUP: a respondent's `hang_up_kind`; `null` is none (every outcome but `partial`). AMENDED 2026-10-29
 * (decoder tightened, design §44.12): the key is required — the api has sent it on every respondent row since plan-033
 * went live (2026-10-28), so an absent key is a malformed answer.
 */
function readHangUpKind(raw: unknown, path: string): SurveyHangUpKindView | null {
  if (raw === null) return null;
  if (raw === "hung_up_before_first_question" || raw === "hung_up_mid_way") return raw;
  throw new MalformedResponseError(path, "a kind of hang-up", raw);
}

function respondentOf(value: unknown, path: string, now: Date, context: SliceContext): SurveyRespondentRowView {
  const one = readRecord(value, path);
  return validatedDisplayView({
    conversationId: readText(one["conversation_id"], `${path}.conversation_id`),
    get startedText() { return context.formatWhen(readText(one["started_at"], `${path}.started_at`), now); },
    outcome: readOutcome(one["outcome"], `${path}.outcome`),
    hangUpKind: readHangUpKind(one["hang_up_kind"], `${path}.hang_up_kind`),
    languageCode: readNullableText(one["language_code"], `${path}.language_code`),
    variant: readVariant(one["variant"], `${path}.variant`),
    isTest: readBoolean(one["is_test"], `${path}.is_test`),
    campaignId: readNullableText(one["campaign_id"], `${path}.campaign_id`),
    campaignName: readNullableText(one["campaign_name"], `${path}.campaign_name`),
    partyMasked: readNullableText(one["party_masked"], `${path}.party_masked`),
    answer: readArray(one["answer"], `${path}.answer`).map((each, index) => givenAnswerOf(each, `${path}.answer[${index}]`)),
  });
}

export function surveyApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<ConsoleGateway, "getSurveyResult" | "listSurveyRespondents" | "requestSurveyDownload" | "getConversationSurveyAnswer"> {
  return {
    async getSurveyResult(voiceAgentId: string, filter: SurveyResultFilterView): Promise<SurveyResultView> {
      const data = readRecord(
        readRecord(
          assertOk(
            await client.getVoiceAgentSurveyResult({
              path: { voice_agent_id: voiceAgentId },
              query: surveyFilterQueryOf(filter, context.now()),
            }),
          ),
          "getVoiceAgentSurveyResult",
        )["data"],
        "getVoiceAgentSurveyResult.data",
      );
      const at = "getVoiceAgentSurveyResult";
      const repeat = readRecord(data["repeat"], `${at}.repeat`);
      return {
        conversationCount: readWholeNumber(data["conversation_count"], `${at}.conversation_count`),
        completedCount: readWholeNumber(data["completed_count"], `${at}.completed_count`),
        partialCount: readWholeNumber(data["partial_count"], `${at}.partial_count`),
        // plan-033 WAPP-HUP: both required since 2026-10-29 (decoder tightened, design §44.12) — the api has sent them
        // since plan-033 went live (2026-10-28), so an absent count is a malformed answer.
        hungUpBeforeFirstQuestionCount: readWholeNumber(
          data["hung_up_before_first_question_count"],
          `${at}.hung_up_before_first_question_count`,
        ),
        hungUpMidWayCount: readWholeNumber(data["hung_up_mid_way_count"], `${at}.hung_up_mid_way_count`),
        declinedCount: readWholeNumber(data["declined_count"], `${at}.declined_count`),
        noAnswerCount: readWholeNumber(data["no_answer_count"], `${at}.no_answer_count`),
        noOutcomeCount: readWholeNumber(data["no_outcome_count"], `${at}.no_outcome_count`),
        completionBasisPoint: readNullableNumber(data["completion_basis_point"], `${at}.completion_basis_point`),
        isTestCount: readWholeNumber(data["is_test_count"], `${at}.is_test_count`),
        hiddenIsTestCount: readWholeNumber(data["hidden_is_test_count"], `${at}.hidden_is_test_count`),
        repeat: {
          answeredTwiceCount: readWholeNumber(repeat["answered_twice_count"], `${at}.repeat.answered_twice_count`),
          answeredThreeOrMoreCount: readWholeNumber(
            repeat["answered_three_or_more_count"],
            `${at}.repeat.answered_three_or_more_count`,
          ),
        },
        variant: readArray(data["variant"], `${at}.variant`).map((one, index) => {
          const path = `${at}.variant[${index}]`;
          const record = readRecord(one, path);
          const variant = readVariant(record["variant"], `${path}.variant`);
          if (variant === null) throw new MalformedResponseError(`${path}.variant`, "A or B", "null");
          return { variant, conversationCount: readWholeNumber(record["conversation_count"], `${path}.conversation_count`) };
        }),
        language: readArray(data["language"], `${at}.language`).map((one, index) => {
          const path = `${at}.language[${index}]`;
          const record = readRecord(one, path);
          return {
            languageCode: readText(record["language_code"], `${path}.language_code`),
            conversationCount: readWholeNumber(record["conversation_count"], `${path}.conversation_count`),
          };
        }),
        campaign: readArray(data["campaign"], `${at}.campaign`).map((one, index) => {
          const path = `${at}.campaign[${index}]`;
          const record = readRecord(one, path);
          return {
            campaignId: readText(record["campaign_id"], `${path}.campaign_id`),
            name: readText(record["name"], `${path}.name`),
            conversationCount: readWholeNumber(record["conversation_count"], `${path}.conversation_count`),
          };
        }),
        question: readArray(data["question"], `${at}.question`).map((one, index) => questionOf(one, `${at}.question[${index}]`)),
      };
    },

    async listSurveyRespondents(
      voiceAgentId: string,
      filter: SurveyResultFilterView,
      outcome: SurveyRespondentOutcomeView | "all",
      cursor: string | null,
    ): Promise<SurveyRespondentPageView> {
      const now = context.now();
      const base = { ...surveyFilterQueryOf(filter, now), outcome, limit: PAGE_LIMIT };
      const body = readRecord(
        assertOk(
          await client.listVoiceAgentSurveyRespondents({
            path: { voice_agent_id: voiceAgentId },
            query: cursor === null ? base : { ...base, cursor },
          }),
        ),
        "listVoiceAgentSurveyRespondents",
      );
      const next = body["next_cursor"];
      return {
        row: readArray(body["data"], "listVoiceAgentSurveyRespondents.data").map((one, index) =>
          respondentOf(one, `listVoiceAgentSurveyRespondents.data[${String(index)}]`, now, context),
        ),
        nextCursor: next === null ? null : readText(next, "listVoiceAgentSurveyRespondents.next_cursor"),
        matchedCount: readWholeNumber(body["matched_count"], "listVoiceAgentSurveyRespondents.matched_count"),
      };
    },

    async requestSurveyDownload(request: SurveyDownloadRequestView): Promise<SurveyDownloadView> {
      const query = surveyFilterQueryOf(request.filter, context.now());
      const data = readRecord(
        readRecord(
          assertOk(
            await client.createVoiceAgentSurveyExport({
              path: { voice_agent_id: request.voiceAgentId },
              body: {
                format: request.format,
                // AMENDED 2026-11-03: a PDF report is worded in the person's display language; no other format reads it.
                ...(request.format === "pdf" ? { display_language: request.displayLanguage } : {}),
                include_recording_link: request.includeRecordingLink,
                include_declined_and_no_answer: request.includeDeclinedAndNoAnswer,
                include_is_test: request.filter.includeIsTest,
                started_from: query.started_from ?? null,
                campaign_id: request.filter.campaignId,
                language_code: request.filter.languageCode,
                variant: request.filter.variant,
              },
            }),
          ),
          "createVoiceAgentSurveyExport",
        )["data"],
        "createVoiceAgentSurveyExport.data",
      );
      return validatedDisplayView({
        downloadUrl: readText(data["download_url"], "createVoiceAgentSurveyExport.download_url"),
        fileExtension: `.${readText(data["file_extension"], "createVoiceAgentSurveyExport.file_extension")}`,
        get expiresAtText() { return midSentence(
          context.formatWhen(readText(data["expires_at"], "createVoiceAgentSurveyExport.expires_at"), context.now()),
        ); },
        rowCount: readWholeNumber(data["row_count"], "createVoiceAgentSurveyExport.row_count"),
        fileName: data["file_name"] === undefined ? null : readText(data["file_name"], "createVoiceAgentSurveyExport.file_name"),
      });
    },

    async getConversationSurveyAnswer(conversationId: string): Promise<ConversationSurveyAnswerView> {
      const at = "getConversationSurveyAnswer";
      const data = readRecord(
        readRecord(assertOk(await client.getConversationSurveyAnswer({ path: { conversation_id: conversationId } })), at)["data"],
        `${at}.data`,
      );
      return {
        conversationId: readText(data["conversation_id"], `${at}.conversation_id`),
        outcome: readOutcome(data["outcome"], `${at}.outcome`),
        variant: readVariant(data["variant"], `${at}.variant`),
        languageCode: readNullableText(data["language_code"], `${at}.language_code`),
        isTest: readBoolean(data["is_test"], `${at}.is_test`),
        answer: readArray(data["answer"], `${at}.answer`).map((one, index) => {
          const path = `${at}.answer[${index}]`;
          const record = readRecord(one, path);
          return {
            questionCode: readText(record["question_code"], `${path}.question_code`),
            instrumentCode: readText(record["instrument_code"], `${path}.instrument_code`),
            questionKind: readKind(record["question_kind"], `${path}.question_kind`),
            variant: readVariant(record["variant"], `${path}.variant`),
            ordinal: readWholeNumber(record["ordinal"], `${path}.ordinal`),
            text: readNullableText(record["text"], `${path}.text`),
            answer: readText(record["answer"], `${path}.answer`),
            mentioned: readTextArray(record["mentioned"], `${path}.mentioned`),
            ...(record["option_label"] === undefined ? {} : { optionLabel: labelsOf(record["option_label"], `${path}.option_label`, "answer", "label") }),
            ...(record["open_text"] === undefined ? {} : { openText: readText(record["open_text"], `${path}.open_text`) }),
            confident: readNullableBoolean(record["confident"], `${path}.confident`),
            languageCode: readText(record["language_code"], `${path}.language_code`),
          };
        }),
      };
    },
  };
}
